import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDiscoveryJobs } from '../hooks/useDiscoveryJobs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Play,
  Target,
  MapPin,
  Calendar,
  Loader2,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import DuplicateReviewModal from '../components/DuplicateReviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_MARKETS = [
  'Westchester, NY',
  'Greenwich, CT',
  'South Bronx, NY',
  'Manhattan, NY',
  'Fairfield County, CT'
];

const DEFAULT_CATEGORIES = [
  'Restaurants',
  'Coffee Shops',
  'Hotels',
  'Catering Companies',
  'Cafeterias',
  'Food Trucks'
];

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    target_markets: [],
    search_categories: [],
    search_keywords: [],
    schedule: 'weekly'
  });
  const [customMarket, setCustomMarket] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [customKeyword, setCustomKeyword] = useState('');

  const { activeJobs, startJob, resumePolling, getJobForCampaign } = useDiscoveryJobs();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [activeReviewId, setActiveReviewId] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();

    // Resume polling for any running or review jobs
    const checkRunningJobs = async () => {
      try {
        const response = await axios.get(`${API}/discovery-jobs`, { withCredentials: true });
        const activeJobsList = response.data.filter(job => job.status === 'running' || job.status === 'review');
        for (const job of activeJobsList) {
          resumePolling(job.campaign_id, job.job_id);
        }
      } catch (error) {
        console.error('Running job check failed:', error);
      }
    };
    checkRunningJobs();
  }, [fetchCampaigns, resumePolling]);

  // Show toasts when jobs complete
  const prevJobs = React.useRef({});
  useEffect(() => {
    for (const [campaignId, job] of Object.entries(activeJobs)) {
      const prev = prevJobs.current[campaignId];
      if (prev?.status === 'running' && job.status === 'completed') {
        toast.success(`Discovery complete! Found ${job.leadsFound} leads.`);
        fetchCampaigns();
      } else if (prev?.status === 'running' && job.status === 'failed') {
        toast.error('Discovery failed. Check logs for details.');
      }
    }
    prevJobs.current = { ...activeJobs };
  }, [activeJobs, fetchCampaigns]);

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    try {
      await axios.post(`${API}/campaigns`, newCampaign, { withCredentials: true });
      toast.success('Campaign created successfully');
      setCreateOpen(false);
      setNewCampaign({ name: '', target_markets: [], search_categories: [], search_keywords: [], schedule: 'weekly' });
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleRunDiscovery = async (campaignId) => {
    try {
      await startJob(campaignId);
      toast.success('Discovery started! Tracking progress below.');
    } catch (error) {
      toast.error('Failed to start discovery');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, { withCredentials: true });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const toggleArrayItem = (array, item, setter, field) => {
    const newArray = array.includes(item) ? array.filter(i => i !== item) : [...array, item];
    setter({ ...newCampaign, [field]: newArray });
  };

  const addCustomItem = (value, field, setter) => {
    if (value.trim() && !newCampaign[field].includes(value.trim())) {
      setter({ ...newCampaign, [field]: [...newCampaign[field], value.trim()] });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Campaigns</h1>
          <p className="text-muted-foreground mt-1">Manage your lead discovery campaigns</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-wheat hover:bg-wheat-dark text-white" data-testid="create-campaign-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Discovery Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div>
                <Label htmlFor="name">Campaign Name *</Label>
                <Input id="name" value={newCampaign.name} onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })} placeholder="e.g., Westchester Coffee Shops Q1" data-testid="input-campaign-name" />
              </div>

              <div>
                <Label className="mb-2 block">Target Markets</Label>
                <p className="text-xs text-muted-foreground mb-2">Select from defaults or add custom markets</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {DEFAULT_MARKETS.map((market) => (
                    <Badge key={market} variant={newCampaign.target_markets.includes(market) ? 'default' : 'outline'} className={`cursor-pointer ${newCampaign.target_markets.includes(market) ? 'bg-forest' : ''}`} onClick={() => toggleArrayItem(newCampaign.target_markets, market, setNewCampaign, 'target_markets')}>
                      {market}
                    </Badge>
                  ))}
                  {newCampaign.target_markets.filter(m => !DEFAULT_MARKETS.includes(m)).map((market) => (
                    <Badge key={market} variant="default" className="bg-wheat cursor-pointer" onClick={() => toggleArrayItem(newCampaign.target_markets, market, setNewCampaign, 'target_markets')}>
                      {market} x
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Add custom market (e.g., Brooklyn, NY)" value={customMarket} onChange={(e) => setCustomMarket(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(customMarket, 'target_markets', setNewCampaign); setCustomMarket(''); } }} />
                  <Button type="button" variant="outline" onClick={() => { addCustomItem(customMarket, 'target_markets', setNewCampaign); setCustomMarket(''); }}>Add</Button>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Business Categories</Label>
                <p className="text-xs text-muted-foreground mb-2">Types of businesses to search for</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {DEFAULT_CATEGORIES.map((category) => (
                    <Badge key={category} variant={newCampaign.search_categories.includes(category) ? 'default' : 'outline'} className={`cursor-pointer ${newCampaign.search_categories.includes(category) ? 'bg-forest' : ''}`} onClick={() => toggleArrayItem(newCampaign.search_categories, category, setNewCampaign, 'search_categories')}>
                      {category}
                    </Badge>
                  ))}
                  {newCampaign.search_categories.filter(c => !DEFAULT_CATEGORIES.includes(c)).map((category) => (
                    <Badge key={category} variant="default" className="bg-wheat cursor-pointer" onClick={() => toggleArrayItem(newCampaign.search_categories, category, setNewCampaign, 'search_categories')}>
                      {category} x
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Add custom category (e.g., Bakery Cafe)" value={customCategory} onChange={(e) => setCustomCategory(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(customCategory, 'search_categories', setNewCampaign); setCustomCategory(''); } }} />
                  <Button type="button" variant="outline" onClick={() => { addCustomItem(customCategory, 'search_categories', setNewCampaign); setCustomCategory(''); }}>Add</Button>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Search Keywords</Label>
                <p className="text-xs text-muted-foreground mb-2">Additional keywords for news and contextual searches</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newCampaign.search_keywords.map((keyword) => (
                    <Badge key={keyword} variant="default" className="bg-forest cursor-pointer" onClick={() => toggleArrayItem(newCampaign.search_keywords, keyword, setNewCampaign, 'search_keywords')}>
                      {keyword} x
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="e.g., new restaurant opening, bakery supplier" value={customKeyword} onChange={(e) => setCustomKeyword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(customKeyword, 'search_keywords', setNewCampaign); setCustomKeyword(''); } }} />
                  <Button type="button" variant="outline" onClick={() => { addCustomItem(customKeyword, 'search_keywords', setNewCampaign); setCustomKeyword(''); }}>Add</Button>
                </div>
              </div>

              <div>
                <Label htmlFor="schedule">Discovery Schedule</Label>
                <Select value={newCampaign.schedule} onValueChange={(value) => setNewCampaign({ ...newCampaign, schedule: value })}>
                  <SelectTrigger data-testid="select-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateCampaign} className="bg-forest hover:bg-forest-dark text-white" data-testid="submit-campaign-btn">Create Campaign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {campaigns.length === 0 ? (
        <Card className="bg-white border-stone-100">
          <CardContent className="text-center py-12">
            <Target className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-4">Create your first discovery campaign to start finding leads</p>
            <Button onClick={() => setCreateOpen(true)} className="bg-forest hover:bg-forest-dark text-white">
              <Plus className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => {
            const job = getJobForCampaign(campaign.campaign_id);
            const isRunning = job && job.status === 'running';
            const needsReview = job && job.status === 'review' && job.reviewId;

            return (
              <Card key={campaign.campaign_id} className="bg-white border-stone-100" data-testid={`campaign-${campaign.campaign_id}`}>
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="font-heading font-semibold text-lg">{campaign.name}</CardTitle>
                    <CardDescription className="mt-1">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {campaign.schedule} discovery
                      </span>
                      {campaign.last_run && (
                        <span className="ml-3">Last run: {new Date(campaign.last_run).toLocaleDateString()}</span>
                      )}
                    </CardDescription>
                  </div>
                  <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'} className={campaign.status === 'active' ? 'bg-green-600' : ''}>
                    {campaign.status}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Target Markets</p>
                      <div className="flex flex-wrap gap-2">
                        {campaign.target_markets.map((market) => (
                          <Badge key={market} variant="outline" className="text-xs"><MapPin className="h-3 w-3 mr-1" />{market}</Badge>
                        ))}
                        {campaign.target_markets.length === 0 && <span className="text-sm text-muted-foreground">No markets selected</span>}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Categories</p>
                      <div className="flex flex-wrap gap-2">
                        {campaign.search_categories.map((category) => (
                          <Badge key={category} variant="secondary" className="text-xs">{category}</Badge>
                        ))}
                        {campaign.search_categories.length === 0 && <span className="text-sm text-muted-foreground">No categories selected</span>}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <div className="text-sm">
                        <span className="font-medium">{campaign.leads_found}</span>
                        <span className="text-muted-foreground"> leads discovered</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCampaign(campaign.campaign_id)} className="text-destructive hover:text-destructive" data-testid={`delete-campaign-${campaign.campaign_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button onClick={() => handleRunDiscovery(campaign.campaign_id)} disabled={isRunning} className="bg-forest hover:bg-forest-dark text-white" data-testid={`run-campaign-${campaign.campaign_id}`}>
                          {isRunning ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Running...</>
                          ) : (
                            <><Play className="mr-2 h-4 w-4" />Run Discovery</>
                          )}
                        </Button>
                      </div>
                    </div>

                    {isRunning && job && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`progress-${campaign.campaign_id}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="relative">
                            <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
                            <div className="absolute inset-0 w-3 h-3 bg-amber-500 rounded-full animate-ping opacity-50" />
                          </div>
                          <span className="font-medium text-amber-800">Discovery in Progress</span>
                        </div>
                        <p className="text-sm text-amber-700 mb-2">{job.progressMessage}</p>
                        <div className="flex items-center justify-between text-xs text-amber-600">
                          <span>
                            {job.leadsFound || job.leadsSoFar} leads found
                            {job.leadsScraped > 0 && <span className="text-amber-500"> ({job.leadsScraped} scraped)</span>}
                          </span>
                          <span>{job.sourcesCompleted?.length || 0} sources completed</span>
                        </div>
                        {job.sourcesCompleted?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {job.sourcesCompleted.map((source) => (
                              <Badge key={source} variant="outline" className="text-xs bg-amber-100 border-amber-300 text-amber-700">
                                {source.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {needsReview && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid={`review-${campaign.campaign_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <div>
                              <p className="font-medium text-sm text-amber-800">
                                {job.leadsPendingReview} potential duplicate{job.leadsPendingReview !== 1 ? 's' : ''} found
                              </p>
                              <p className="text-xs text-amber-600">{job.progressMessage}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => { setActiveReviewId(job.reviewId); setReviewOpen(true); }}
                            data-testid={`review-btn-${campaign.campaign_id}`}
                          >
                            Review Duplicates
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DuplicateReviewModal
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        reviewId={activeReviewId}
        onResolved={() => { fetchCampaigns(); }}
      />
    </div>
  );
};

export default Campaigns;
